<script lang="ts">
/**
 * @module routes/[[locale=locale]]/+page
 * @description Home / landing page of the portfolio website. Displays a fullscreen
 * hero section with animated typing effects for the greeting, name, and professional
 * title. The name is rendered with an `AuroraText` gradient animation using
 * Nord-inspired colors (#C16069 red / #A2BF8A green).
 *
 * Below the hero text, four call-to-action buttons link to the main sections:
 * About Me, Projects, CV, and Contact. All links are locale-aware via
 * `getLocalizedUrl()`, ensuring correct routing under the optional locale prefix.
 *
 * The page uses multiple Intlayer content dictionaries:
 * - `aboutme` - for name, greeting, title
 * - `sites` - for navigation labels
 * - `layout` - for the site-wide title
 * - `recommendation` - for CTA button labels
 *
 * Hero landing page with centered content following iOS onboarding screen
 * patterns. Typography scales responsively across Apple viewport classes
 * (4xl iPhone to 8xl Mac).
 *
 * @see {@link $lib/components/AuroraText.svelte} for the gradient text animation
 * @see {@link $lib/components/TypingAnimation.svelte} for the typewriter effect
 */

import { t, locale, getLocalizedUrl, type LocaleCode } from "$lib/i18n";
import AuroraText from "$lib/components/AuroraText.svelte";
import { User, FolderOpen, FileText, Mail } from "@lucide/svelte";
import * as Button from "$lib/components/ui/button";
import { createWebHaptics } from "web-haptics/svelte";
import { onDestroy } from "svelte";
const { trigger, destroy } = createWebHaptics();
onDestroy(destroy);
</script>

<svelte:head>
  <title>{$t("aboutme.name")} — {$t("aboutme.title")} | {$t("layout.title")}</title>
  <meta name="description" content={$t("aboutme.title")} />
</svelte:head>

<div class="flex flex-col items-center justify-center min-h-[90vh] w-full relative px-6 lg:px-8">
  <!-- Hero Content -->
  <div class="flex flex-col items-center gap-6 text-center">
    <!-- Animated Greeting -->
    <div class="text-muted-foreground text-xl md:text-2xl lg:text-3xl animate-fade-in">
      {$t("aboutme.hello")} 󱠢 {$t("aboutme.iam")}
    </div>

    <div class="w-[85vw] max-w-7xl shrink-0 rounded-full" style="height: 3px; background: linear-gradient(to right, #2e3440, #eceff4, #2e3440);"></div>

    <!-- Animated Name with Gradient -->
    <AuroraText
      class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold rounded-sm"
      colors={['#C16069', '#A2BF8A', '#C16069', '#A2BF8A', '#C16069', '#A2BF8A', '#C16069']}
      speed={3}
    >
      {$t("aboutme.name")}
    </AuroraText>

    <div class="w-[85vw] max-w-7xl shrink-0 rounded-full" style="height: 3px; background: linear-gradient(to right, #2e3440, #eceff4, #2e3440);"></div>

    <!-- Animated Title/Tagline -->
    <div class="text-2xl md:text-3xl lg:text-4xl text-muted-foreground mt-4">
      {$t("aboutme.title")}
    </div>

    <!-- CTA Buttons -->
    <div class="flex flex-wrap gap-4 mt-12 justify-center">
      <Button.Root onclick={
        () => trigger([
{ duration: 60, intensity: 1 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60, intensity: 1 },
])}
      href={getLocalizedUrl('/aboutme', $locale)} variant="default" size="lg" class="group gap-2">
        <User class="w-4 h-4 group-hover:scale-110 transition-transform" />
        {$t("recommendation.aboutme.action.label")}
      </Button.Root>
      <Button.Root onclick={
        () => trigger([
{ duration: 60, intensity: 1 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60, intensity: 1 },
])}
      href={getLocalizedUrl('/projects', $locale)} variant="secondary" size="lg" class="group gap-2">
        <FolderOpen class="w-4 h-4 group-hover:scale-110 transition-transform" />
        {$t("recommendation.projects.action.label")}
      </Button.Root>
      <Button.Root onclick={
        () => trigger([
{ duration: 60, intensity: 1 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60, intensity: 1 },
])}
      href={getLocalizedUrl('/cv', $locale)} variant="outline" size="lg" class="my-glass group gap-2">
        <FileText class="w-4 h-4 group-hover:scale-110 transition-transform" />
        {$t("recommendation.cv.action.label")}
      </Button.Root>
      <Button.Root onclick={
        () => trigger([
{ duration: 60, intensity: 1 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60 },
{ delay: 30, duration: 60, intensity: 0.75 },
{ delay: 30, duration: 60, intensity: 1 },
])}
      href={getLocalizedUrl('/contact', $locale)} variant="ghost" size="lg" class="group gap-2">
        <Mail class="w-4 h-4 group-hover:scale-110 transition-transform" />
        {$t("sites.contact")}
      </Button.Root>
    </div>
  </div>
</div>
